import { useEffect, useRef, useState } from 'react'

import { useFilteredIncidentColumns } from '../hooks/useFilteredIncidentColumns'
import { useIncidentWithDisplayId } from '../hooks/useIncidentWithDisplayId'
import { useCanBulkAction } from '@/hooks/useBulkAction'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import { usePermissions } from '@/hooks/usePermissions'
import {
  PaginationState,
  type RowSelectionState,
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useRouter } from 'next/compat/router'
import { BiCheck, BiX } from 'react-icons/bi'
import { useDispatch, useSelector } from 'react-redux'

import { Box, useDisclosure } from '@chakra-ui/react'
import mixpanel from 'mixpanel-browser'

import { ActionBar } from '@/components/layouts/ActionBar'
import {
  PageSizeSelector,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
  TableSpinner,
} from '@/components/ui'
import { extendMutedFilter } from '@/redux/incidents/conversion'
import {
  selectIncidentsFilterInput,
  selectIncidentsPageSize,
  setIncidentsPageSize,
} from '@/redux/incidents/incidentsSlice'
import { selectIsIncidentDrawerOpen } from '@/redux/ui/uiSlice'

import { IncidentsListType, RefetchIncidentsFnType } from '../types/types'
import { INCIDENT_TABLE_HEIGHT, formatTableData } from '../utils/helpers'
import { getOrderBy } from '../utils/query'
import { BulkActionModal } from './BulkActionModal'

interface IncidentsTableIProps {
  incidents?: IncidentsListType[]
  totalIncidents?: number
  refetch: RefetchIncidentsFnType
}

export const IncidentsTable = ({
  incidents,
  totalIncidents,
  refetch,
}: IncidentsTableIProps) => {
  // region State & Data management
  const dispatch = useDispatch()
  const pageSize = useSelector(selectIncidentsPageSize)
  const filters = useSelector(selectIncidentsFilterInput)
  const pageCount = Math.ceil(totalIncidents / pageSize)
  const [isLoading, setIsLoading] = useState(false)
  const { openIncident, setDisplayId, openAlertIncidentDrawer } =
    useOpenIncident()
  const { query } = useRouter()
  const isDrawerOpen = useSelector(selectIsIncidentDrawerOpen)
  const { queryIncidentId } = useIncidentWithDisplayId({
    displayId: query?.displayId,
  })
  const { shouldOnlyShowAlarmed } = usePermissions()
  const tableRef = useRef()

  const {
    isOpen: isBulkModalOpen,
    onOpen: onBulkModalOpen,
    onClose: onBulkModalClose,
  } = useDisclosure()

  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const isQuickReportWhiteListEnabled = useFeatureFlag(
    FeatureFlag.quickReportWhitelist
  )

  const tableData = formatTableData(incidents, isQuickReportWhiteListEnabled)
  const table = useReactTable({
    data: tableData,
    columns: useFilteredIncidentColumns(),
    getRowId: (row) => row.id,
    state: {
      pagination: paginationState,
      sorting,
      rowSelection,
    },
    manualPagination: true,
    manualSorting: true,
    pageCount,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: (updater) => {
      setRowSelection({})
      setPaginationState(updater)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  })

  useEffect(() => {
    setIsLoading(true)
    refetch({
      filter: extendMutedFilter(filters, shouldOnlyShowAlarmed),
      first: paginationState.pageIndex,
      last: paginationState.pageSize,
      orderBy: getOrderBy(sorting?.[0]),
    }).then(() => {
      setIsLoading(false)
    })
  }, [paginationState, JSON.stringify(sorting)])

  useEffect(() => {
    table.resetPageIndex()
    setIsLoading(true)
    refetch({
      filter: extendMutedFilter(filters, shouldOnlyShowAlarmed),
      first: paginationState.pageIndex,
      last: paginationState.pageSize,
      orderBy: getOrderBy(sorting?.[0]),
    }).then(() => {
      setIsLoading(false)
    })
  }, [JSON.stringify(filters)])

  useEffect(() => {
    if (queryIncidentId) {
      openAlertIncidentDrawer(queryIncidentId)
    }
  }, [queryIncidentId])

  const onPageResize = (pageIndex: number) => {
    dispatch(setIncidentsPageSize(pageIndex))
  }

  // This ref keeps track of the previous drawer state for comparison
  const prevDrawerOpenState = useRef(isDrawerOpen)
  /**
   * Clear the URL param when the drawer is no longer open
   */
  useEffect(() => {
    if (!isDrawerOpen && prevDrawerOpenState.current) {
      setDisplayId(null)
    }
    prevDrawerOpenState.current = isDrawerOpen
  }, [isDrawerOpen, setDisplayId])

  const handleSelectItem = (id: string) => {
    const selectedIncident = incidents.find((inc) => inc.id === id)
    mixpanel.track('Button Press: Incident Row, Incidents View', {
      incident_id: selectedIncident?.displayId,
      incident_status: selectedIncident?.status,
      incident_type: selectedIncident?.status,
    })
    openIncident({
      incidentId: id,
      displayId: selectedIncident?.displayId,
    })
  }

  useEffect(() => {
    const selectedIds: string[] = table
      .getSelectedRowModel()
      .rows.map((row) => {
        return row.original.id
      })

    setSelectedIncidentIds(selectedIds)
  }, [table.getState().rowSelection])

  const [selectedIncidentIds, setSelectedIncidentIds] = useState([])

  const shouldShowBulkAction: boolean = useCanBulkAction()
  // endregion

  return (
    <Box ref={tableRef}>
      {/* region Bulk resolve ActionBar */}
      {selectedIncidentIds.length > 0 && shouldShowBulkAction && (
        <ActionBar
          label={`${selectedIncidentIds.length} ${
            selectedIncidentIds.length === 1 ? 'incident' : 'incidents'
          } selected`}
          primaryButton={{
            icon: BiCheck,
            text: 'Bulk Resolve',
            onClick: onBulkModalOpen,
          }}
          secondaryButton={{
            icon: BiX,
            text: 'Clear All',
            onClick: () => {
              table.setRowSelection({})
            },
          }}
        />
      )}
      {/* endregion */}

      <Table
        tableContainerStyles={{
          maxH: INCIDENT_TABLE_HEIGHT,
          mb: '42px',
        }}
      >
        <TableHeader
          paddingX='16px'
          rowBgColor='#E1E9FC'
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#E1E9FC',
          }}
          table={table}
        />
        {/* region Table Body */}
        {isLoading ? (
          <TableSpinner height={INCIDENT_TABLE_HEIGHT} />
        ) : (
          <TableBody
            borderBottomColor='border.100'
            onRowClick={handleSelectItem}
            paddingX='16px'
            paddingY='8px'
            rowModel={table.getRowModel()}
          />
        )}
        {/* endregion */}
        {/* region Table Footer */}
        <TableFooter
          onGoToPage={table.setPageIndex}
          onNextPage={table.nextPage}
          onPreviousPage={table.previousPage}
          pageCount={pageCount}
          pageIndex={paginationState.pageIndex}
          pageSize={paginationState.pageSize}
          rightContent={
            <PageSizeSelector
              onClick={onPageResize}
              pageSize={table.getState().pagination.pageSize}
              setPageSize={table.setPageSize}
              title='Incidents per page:'
            />
          }
          total={totalIncidents}
        />
        {/* endregion */}
      </Table>

      {/* region Bulk Action Modal */}
      <BulkActionModal
        incidentIds={selectedIncidentIds}
        isOpen={isBulkModalOpen}
        onClose={onBulkModalClose}
        onSuccess={() => {
          table.setRowSelection({})
        }}
      />
      {/* endregion */}
    </Box>
  )
}
